// src/Customer/CustomerDashboard/CustomerProfile.js

import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";

const STORAGE_KEY = "gocourier_customer_profile_v1";

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
];

const TIME_ZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Phoenix",
  "America/Anchorage",
  "Pacific/Honolulu",
];

const LANGUAGES = [
  { value: "en", label: "English" },
  { value: "es", label: "Spanish" },
  { value: "fr", label: "French" },
  { value: "ru", label: "Russian" },
  { value: "de", label: "German" },
  { value: "zh", label: "Chinese" },
  { value: "ar", label: "Arabic" },
  { value: "pt", label: "Portuguese" },
  { value: "it", label: "Italian" },
];

const fieldStyle = {
  "& .MuiInputBase-root": {
    minHeight: 56,
  },
};

function safeParse(json) {
  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function onlyDigits(value) {
  return (value || "").replace(/[^\d]/g, "");
}

function formatPhone(value) {
  const digits = onlyDigits(value).slice(0, 10);

  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;

  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function isEmail(value) {
  if (!value) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function isUSZip(value) {
  if (!value) return true;
  return /^\d{5}(-\d{4})?$/.test(value);
}

function makeEmptyAddress() {
  return {
    id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
    label: "",
    recipientName: "",
    recipientPhone: "",
    line1: "",
    line2: "",
    city: "",
    state: "",
    zip: "",
    instructions: "",
    isDefaultPickup: false,
    isDefaultDropoff: false,
  };
}

function makeDefaultProfile() {
  return {
    customerType: "Individual",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",

    businessName: "",
    businessWebsite: "",
    businessEIN: "",
    billingEmail: "",
    billingPhone: "",

    addresses: [makeEmptyAddress()],

    language: "en",
    timeZone: "America/Denver",
    measurementUnits: "imperial",

    notifyEmail: true,
    notifySMS: false,
    notifyPush: true,
    marketingOptIn: false,
    allowUsageData: true,

    updatedAt: null,
  };
}

const CustomerProfile = () => {
  const [profile, setProfile] = useState(makeDefaultProfile());
  const [errors, setErrors] = useState({});
  const [saved, setSaved] = useState(false);
  const [errorBanner, setErrorBanner] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const stored = safeParse(localStorage.getItem(STORAGE_KEY));

    if (stored) {
      setProfile({
        ...makeDefaultProfile(),
        ...stored,
        addresses:
          stored.addresses && stored.addresses.length
            ? stored.addresses
            : [makeEmptyAddress()],
      });
    }
  }, []);

  const isBusiness = profile.customerType === "Business";

  const setField = (name, value) => {
    setProfile((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: "" }));
  };

  const setAddressField = (addressId, name, value) => {
    setProfile((current) => ({
      ...current,
      addresses: current.addresses.map((address) =>
        address.id === addressId ? { ...address, [name]: value } : address
      ),
    }));

    setErrors((current) => ({
      ...current,
      [`addr:${addressId}:${name}`]: "",
    }));
  };

  const setDefaultAddress = (addressId, type) => {
    setProfile((current) => ({
      ...current,
      addresses: current.addresses.map((address) => {
        if (type === "pickup") {
          return { ...address, isDefaultPickup: address.id === addressId };
        }

        return { ...address, isDefaultDropoff: address.id === addressId };
      }),
    }));
  };

  const addAddress = () => {
    setProfile((current) => ({
      ...current,
      addresses: [...current.addresses, makeEmptyAddress()],
    }));
  };

  const removeAddress = (addressId) => {
    setProfile((current) => {
      const remaining = current.addresses.filter(
        (address) => address.id !== addressId
      );

      return {
        ...current,
        addresses: remaining.length ? remaining : [makeEmptyAddress()],
      };
    });
  };

  const validateProfile = (nextProfile) => {
    const nextErrors = {};

    if (!nextProfile.firstName.trim()) {
      nextErrors.firstName = "First name is required.";
    }

    if (!nextProfile.lastName.trim()) {
      nextErrors.lastName = "Last name is required.";
    }

    if (!nextProfile.email.trim()) {
      nextErrors.email = "Email is required.";
    } else if (!isEmail(nextProfile.email.trim())) {
      nextErrors.email = "Enter a valid email.";
    }

    if (
      nextProfile.phone &&
      onlyDigits(nextProfile.phone).length > 0 &&
      onlyDigits(nextProfile.phone).length < 10
    ) {
      nextErrors.phone = "Enter a valid 10-digit phone.";
    }

    if (nextProfile.customerType === "Business") {
      if (!nextProfile.businessName.trim()) {
        nextErrors.businessName = "Business name is required.";
      }

      if (nextProfile.billingEmail && !isEmail(nextProfile.billingEmail)) {
        nextErrors.billingEmail = "Enter a valid billing email.";
      }

      if (
        nextProfile.billingPhone &&
        onlyDigits(nextProfile.billingPhone).length > 0 &&
        onlyDigits(nextProfile.billingPhone).length < 10
      ) {
        nextErrors.billingPhone = "Enter a valid 10-digit billing phone.";
      }
    }

    nextProfile.addresses.forEach((address) => {
      const key = (field) => `addr:${address.id}:${field}`;

      const addressStarted =
        address.label ||
        address.recipientName ||
        address.recipientPhone ||
        address.line1 ||
        address.line2 ||
        address.city ||
        address.state ||
        address.zip ||
        address.instructions;

      if (addressStarted) {
        if (!address.line1.trim()) {
          nextErrors[key("line1")] = "Address line 1 is required.";
        }

        if (!address.city.trim()) {
          nextErrors[key("city")] = "City is required.";
        }

        if (!address.state.trim()) {
          nextErrors[key("state")] = "State is required.";
        }

        if (!address.zip.trim()) {
          nextErrors[key("zip")] = "ZIP is required.";
        } else if (!isUSZip(address.zip)) {
          nextErrors[key("zip")] = "Enter a valid ZIP.";
        }

        if (
          address.recipientPhone &&
          onlyDigits(address.recipientPhone).length > 0 &&
          onlyDigits(address.recipientPhone).length < 10
        ) {
          nextErrors[key("recipientPhone")] = "Enter a valid 10-digit phone.";
        }
      }
    });

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = () => {
    setErrorBanner("");
    setIsSaving(true);

    const nextProfile = {
      ...profile,
      updatedAt: new Date().toISOString(),
    };

    if (!validateProfile(nextProfile)) {
      setErrorBanner("Please review the highlighted fields.");
      setIsSaving(false);
      return;
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(nextProfile));
    setProfile(nextProfile);
    setSaved(true);
    setIsSaving(false);
  };

  return (
    <Box sx={{ maxWidth: 1080, mx: "auto", py: { xs: 2, md: 4 } }}>
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, letterSpacing: "-0.02em", mb: 1 }}
        >
          Profile
        </Typography>

        <Typography
          sx={{
            color: "text.secondary",
            fontSize: "1rem",
            lineHeight: 1.6,
            maxWidth: 820,
          }}
        >
          Manage your profile, saved locations, and logistics preferences.
        </Typography>
      </Box>

      {errorBanner && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {errorBanner}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          border: "1px solid #e5e7eb",
          borderRadius: 3,
          p: { xs: 3, md: 4 },
          mb: 3,
          boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2.5 }}>
          Identity & Contact
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              label="Customer Type"
              value={profile.customerType}
              onChange={(event) => setField("customerType", event.target.value)}
              sx={fieldStyle}
            >
              <MenuItem value="Individual">Individual</MenuItem>
              <MenuItem value="Business">Business</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="First Name"
              value={profile.firstName}
              onChange={(event) => setField("firstName", event.target.value)}
              error={!!errors.firstName}
              helperText={errors.firstName || " "}
              sx={fieldStyle}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Last Name"
              value={profile.lastName}
              onChange={(event) => setField("lastName", event.target.value)}
              error={!!errors.lastName}
              helperText={errors.lastName || " "}
              sx={fieldStyle}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Email"
              value={profile.email}
              onChange={(event) => setField("email", event.target.value)}
              error={!!errors.email}
              helperText={errors.email || " "}
              sx={fieldStyle}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Phone"
              value={profile.phone}
              onChange={(event) =>
                setField("phone", formatPhone(event.target.value))
              }
              error={!!errors.phone}
              helperText={errors.phone || " "}
              sx={fieldStyle}
            />
          </Grid>
        </Grid>
      </Paper>

      {isBusiness && (
        <Paper
          elevation={0}
          sx={{
            border: "1px solid #e5e7eb",
            borderRadius: 3,
            p: { xs: 3, md: 4 },
            mb: 3,
            boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2.5 }}>
            Business Details
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Business Name"
                value={profile.businessName}
                onChange={(event) => setField("businessName", event.target.value)}
                error={!!errors.businessName}
                helperText={errors.businessName || " "}
                sx={fieldStyle}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Business Website"
                value={profile.businessWebsite}
                onChange={(event) =>
                  setField("businessWebsite", event.target.value)
                }
                sx={fieldStyle}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="EIN"
                value={profile.businessEIN}
                onChange={(event) => setField("businessEIN", event.target.value)}
                sx={fieldStyle}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Billing Email"
                value={profile.billingEmail}
                onChange={(event) => setField("billingEmail", event.target.value)}
                error={!!errors.billingEmail}
                helperText={errors.billingEmail || " "}
                sx={fieldStyle}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Billing Phone"
                value={profile.billingPhone}
                onChange={(event) =>
                  setField("billingPhone", formatPhone(event.target.value))
                }
                error={!!errors.billingPhone}
                helperText={errors.billingPhone || " "}
                sx={fieldStyle}
              />
            </Grid>
          </Grid>
        </Paper>
      )}

      <Paper
        elevation={0}
        sx={{
          border: "1px solid #e5e7eb",
          borderRadius: 3,
          p: { xs: 3, md: 4 },
          mb: 3,
          boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Address Book
            </Typography>

            <Typography color="text.secondary" sx={{ mt: 0.5 }}>
              Save pickup and drop-off locations for faster load creation.
            </Typography>
          </Box>

          <Button
            variant="outlined"
            onClick={addAddress}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Add Address
          </Button>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {profile.addresses.map((address, index) => {
          const getError = (field) => errors[`addr:${address.id}:${field}`];

          return (
            <Box
              key={address.id}
              sx={{ mb: index === profile.addresses.length - 1 ? 0 : 3 }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography sx={{ fontWeight: 700 }}>
                  Address #{index + 1}
                </Typography>

                <Button
                  color="error"
                  disabled={profile.addresses.length === 1}
                  onClick={() => removeAddress(address.id)}
                  sx={{ textTransform: "none", fontWeight: 700 }}
                >
                  Remove
                </Button>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Label"
                    placeholder="Home, office, warehouse"
                    value={address.label}
                    onChange={(event) =>
                      setAddressField(address.id, "label", event.target.value)
                    }
                    sx={fieldStyle}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Recipient Name"
                    value={address.recipientName}
                    onChange={(event) =>
                      setAddressField(
                        address.id,
                        "recipientName",
                        event.target.value
                      )
                    }
                    sx={fieldStyle}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Recipient Phone"
                    value={address.recipientPhone}
                    onChange={(event) =>
                      setAddressField(
                        address.id,
                        "recipientPhone",
                        formatPhone(event.target.value)
                      )
                    }
                    error={!!getError("recipientPhone")}
                    helperText={getError("recipientPhone") || " "}
                    sx={fieldStyle}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Address Line 1"
                    value={address.line1}
                    onChange={(event) =>
                      setAddressField(address.id, "line1", event.target.value)
                    }
                    error={!!getError("line1")}
                    helperText={getError("line1") || " "}
                    sx={fieldStyle}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Address Line 2"
                    value={address.line2}
                    onChange={(event) =>
                      setAddressField(address.id, "line2", event.target.value)
                    }
                    sx={fieldStyle}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="City"
                    value={address.city}
                    onChange={(event) =>
                      setAddressField(address.id, "city", event.target.value)
                    }
                    error={!!getError("city")}
                    helperText={getError("city") || " "}
                    sx={fieldStyle}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    fullWidth
                    label="State"
                    value={address.state}
                    onChange={(event) =>
                      setAddressField(address.id, "state", event.target.value)
                    }
                    error={!!getError("state")}
                    helperText={getError("state") || " "}
                    sx={fieldStyle}
                  >
                    {US_STATES.map((state) => (
                      <MenuItem key={state} value={state}>
                        {state}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="ZIP"
                    value={address.zip}
                    onChange={(event) =>
                      setAddressField(address.id, "zip", event.target.value)
                    }
                    error={!!getError("zip")}
                    helperText={getError("zip") || " "}
                    sx={fieldStyle}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label="Instructions"
                    placeholder="Gate code, dock details, pickup notes, or delivery instructions"
                    value={address.instructions}
                    onChange={(event) =>
                      setAddressField(
                        address.id,
                        "instructions",
                        event.target.value
                      )
                    }
                  />
                </Grid>

                <Grid item xs={12}>
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      gap: 1,
      mt: 0.5,
    }}
  >
    <FormControlLabel
      sx={{
        ml: 0,
        mr: 0,
        alignItems: "center",
      }}
      control={
        <Checkbox
          checked={address.isDefaultPickup}
          onChange={() => setDefaultAddress(address.id, "pickup")}
          sx={{ p: 0.75 }}
        />
      }
      label="Default pickup"
    />

    <FormControlLabel
      sx={{
        ml: 0,
        mr: 0,
        alignItems: "center",
      }}
      control={
        <Checkbox
          checked={address.isDefaultDropoff}
          onChange={() => setDefaultAddress(address.id, "dropoff")}
          sx={{ p: 0.75 }}
        />
      }
      label="Default drop-off"
    />
  </Box>
</Grid>
              </Grid>

              {index < profile.addresses.length - 1 && (
                <Divider sx={{ mt: 3 }} />
              )}
            </Box>
          );
        })}
      </Paper>

      <Paper
        elevation={0}
        sx={{
          border: "1px solid #e5e7eb",
          borderRadius: 3,
          p: { xs: 3, md: 4 },
          mb: 3,
          boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2.5 }}>
          Preferences
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              label="Language"
              value={profile.language}
              onChange={(event) => setField("language", event.target.value)}
              sx={fieldStyle}
            >
              {LANGUAGES.map((language) => (
                <MenuItem key={language.value} value={language.value}>
                  {language.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              label="Time Zone"
              value={profile.timeZone}
              onChange={(event) => setField("timeZone", event.target.value)}
              sx={fieldStyle}
            >
              {TIME_ZONES.map((zone) => (
                <MenuItem key={zone} value={zone}>
                  {zone}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              label="Units"
              value={profile.measurementUnits}
              onChange={(event) =>
                setField("measurementUnits", event.target.value)
              }
              sx={fieldStyle}
            >
              <MenuItem value="imperial">Imperial (mi, lb)</MenuItem>
              <MenuItem value="metric">Metric (km, kg)</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ display: "flex", justifyContent: "flex-end", pb: 2 }}>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isSaving}
          sx={{ py: 1.3, px: 4, textTransform: "none", fontWeight: 700 }}
        >
          {isSaving ? "Saving..." : "Save Profile"}
        </Button>
      </Box>

      <Snackbar
        open={saved}
        autoHideDuration={3500}
        onClose={() => setSaved(false)}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setSaved(false)}
        >
          Profile saved.
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomerProfile;